import * as kinds from '@nostr/tools/kinds'
import ContentPreview from '@/components/ContentPreview'
import { FormattedTimestamp } from '@/components/FormattedTimestamp'
import ParentNotePreview from '@/components/ParentNotePreview'
import UserAvatar from '@/components/UserAvatar'
import Username from '@/components/Username'
import { Skeleton } from '@/components/ui/skeleton'
import {
  ArrowReloadHorizontalIcon,
  AtIcon,
  BubbleChatIcon,
  NewsIcon,
  QuoteUpIcon
} from '@hugeicons/core-free-icons'
import { HugeiconsIcon } from '@hugeicons/react'
import { getParents } from '@/lib/event'
import { cn } from '@/lib/utils'
import { toNote } from '@/lib/link'
import { useSecondaryPage } from '@/PageManager'
import { NostrEvent } from '@nostr/tools/wasm'
import { parse } from '@nostr/tools/nip27'
import { useEffect, useState } from 'react'
import { AddressPointer, EventPointer } from '@nostr/tools/nip19'
import { useCurrentPubkey } from '@/atoms/session'

export type TShortNoteViewStyle = 'compact' | 'detailed'

export function ShortNoteView({
  event,
  style,
  className,
  onClick,
  bold = false,
  user,
  timestamp,
  isRepostFrom
}: {
  event: NostrEvent
  style: TShortNoteViewStyle
  className?: string
  onClick?: () => void
  bold?: boolean
  user?: string
  timestamp?: number
  isRepostFrom?: string
}) {
  const { push } = useSecondaryPage()
  const pubkey = useCurrentPubkey()

  const userId = user ?? event.pubkey
  const ts = timestamp ?? event.created_at
  const handleClick = onClick ?? (() => push(toNote(event)))

  let iconNode: JSX.Element
  let isReply: boolean = false

  if (isRepostFrom) {
    isReply = false
    iconNode = (
      <HugeiconsIcon
        icon={ArrowReloadHorizontalIcon}
        size={24}
        className={isRepostFrom === pubkey ? 'text-pink-400' : 'text-blue-400'}
      />
    )
  } else {
    let isQuote: boolean = false
    let quotingUs: boolean | undefined = false
    let replyingToUs: boolean = false
    let mentioningUs: boolean = false
    const fromUs: boolean = event.pubkey === pubkey

    if (event.kind === kinds.Comment || event.kind === kinds.VoiceComment) {
      isReply = true
    } else if (event.kind === kinds.ShortTextNote) {
      for (let i = 0; i < event.tags.length; i++) {
        const tag = event.tags[i]
        if (tag[0] === 'e') {
          isReply = true
          if (tag[3] === 'reply' && tag[4] === pubkey) replyingToUs = true
        }
      }
    } else {
      isReply = false
    }

    for (const block of parse(event.content)) {
      if (block.type === 'reference') {
        if ('id' in block.pointer) {
          isQuote = true
          if (block.pointer.author === pubkey) quotingUs = true
          else quotingUs = undefined
        } else if ('identifier' in block.pointer) {
          isQuote = true
          if (block.pointer.pubkey === pubkey) quotingUs = true
        } else {
          if (block.pointer.pubkey === pubkey) mentioningUs = true
        }
      }
    }

    if (quotingUs === undefined) {
      if (fromUs || mentioningUs || replyingToUs) {
        quotingUs = false
      } else {
        for (let i = 0; i < event.tags.length; i++) {
          const tag = event.tags[i]
          if (tag[0] === 'p' && tag[1] === pubkey) {
            quotingUs = true
          }
        }
      }
    }

    iconNode = replyingToUs ? (
      <HugeiconsIcon icon={BubbleChatIcon} size={24} className="text-pink-400" />
    ) : mentioningUs ? (
      <HugeiconsIcon icon={AtIcon} size={24} className="text-pink-400" />
    ) : isQuote ? (
      <HugeiconsIcon
        icon={QuoteUpIcon}
        size={24}
        className={quotingUs ? 'text-pink-400' : 'text-blue-400'}
      />
    ) : isReply ? (
      <HugeiconsIcon icon={BubbleChatIcon} size={24} className="text-blue-400" />
    ) : (
      <HugeiconsIcon
        icon={NewsIcon}
        size={24}
        className={fromUs ? 'text-pink-400' : 'text-blue-400'}
      />
    )
  }

  const [parent, setParent] = useState<EventPointer | AddressPointer | undefined>()
  useEffect(() => {
    if (isReply) {
      const parents = getParents(event)
      setParent(parents[0] as EventPointer | AddressPointer | undefined)
    } else {
      setParent(undefined)
    }
  }, [event, isReply])

  if (style === 'compact') {
    return (
      <div
        className={cn('flex items-center gap-2 cursor-pointer py-2 px-4', className)}
        onClick={handleClick}
      >
        <UserAvatar user={userId} size="small" />
        {iconNode}
        <ContentPreview
          className={cn('truncate flex-1 w-0', bold ? 'font-semibold' : 'text-muted-foreground')}
          event={event}
        />
        <div className="text-muted-foreground shrink-0">
          <FormattedTimestamp timestamp={ts} short />
        </div>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'transition-colors hover:bg-muted/30 active:bg-muted/30 flex items-start gap-2 cursor-pointer py-2 px-4 border-b',
        className
      )}
      onClick={handleClick}
    >
      <div className="flex gap-2 items-center mt-1.5 shrink-0">
        {iconNode}
        <UserAvatar user={userId} size="medium" />
      </div>
      <div className="flex-1 w-0">
        <div className="flex items-center justify-between gap-1">
          <Username
            user={userId}
            className="flex-1 max-w-fit truncate font-semibold"
            skeletonClassName="h-4"
          />
        </div>
        {parent && (
          <ParentNotePreview
            first
            parent={parent}
            onClick={(e) => {
              e.stopPropagation()
              push(toNote(parent))
            }}
          />
        )}
        <ContentPreview
          className={cn('line-clamp-2', !bold && 'text-muted-foreground')}
          event={event}
        />
        <FormattedTimestamp timestamp={ts} className="shrink-0 text-muted-foreground text-sm" />
      </div>
    </div>
  )
}

export function ShortNoteViewSkeleton({ style }: { style: TShortNoteViewStyle }) {
  if (style === 'compact') {
    return (
      <div className="flex gap-2 items-center h-11 py-2 px-4">
        <Skeleton className="w-7 h-7 rounded-full" />
        <Skeleton className="h-6 flex-1 w-0" />
      </div>
    )
  }

  return (
    <div className="flex items-start gap-2 cursor-pointer py-2 px-4">
      <div className="flex gap-2 items-center mt-1.5">
        <Skeleton className="w-9 h-9 rounded-full" />
      </div>
      <div className="flex-1 w-0">
        <div className="py-1">
          <Skeleton className="w-16 h-4" />
        </div>
        <div className="py-1">
          <Skeleton className="w-full h-4" />
        </div>
        <div className="py-1">
          <Skeleton className="w-12 h-4" />
        </div>
      </div>
    </div>
  )
}
